import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import React from "react";
import { IInstrument } from "../../../clients/answeringClient";
import {
  IResellerClient,
  ResellerManagementController,
} from "../../controllers/ResellerManagementController";

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  preselectedClient?: IResellerClient | null;
  onClose: () => void;
  onConfirm: (data: {
    clientId: number;
    clientUid: string;
    items: { instrumentId: number; quantity: number }[];
  }) => void;

}

interface State {
  selectedClient: IResellerClient | null;
  clientInput: string;
  clientOptions: IResellerClient[];
  clientLoading: boolean;

  selectedInstruments: IInstrument[];
  instrumentInput: string;
  instrumentOptions: IInstrument[];
  instrumentLoading: boolean;

  quantities: Partial<Record<number, number>>;
}

export class AssignLicenseModal extends React.Component<Props, State> {
  private controller = new ResellerManagementController();
  private clientFetchDebounce?: NodeJS.Timeout;
  private instrumentFetchDebounce?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedClient: null,
      clientInput: "",
      clientOptions: [],
      clientLoading: false,

      selectedInstruments: [],
      instrumentInput: "",
      instrumentOptions: [],
      instrumentLoading: false,

      quantities: {},
    };
  }

  componentDidUpdate(prev: Props) {
    const { isOpen, preselectedClient } = this.props;
    if (isOpen && !prev.isOpen) {
      if (preselectedClient) {
        this.setState({
          selectedClient: preselectedClient,
          clientOptions: [preselectedClient],
          clientInput: preselectedClient.clientName,
          clientLoading: false,
        });
      } else {
        this.fetchClients("");
      }

      this.setState(
        {
          selectedInstruments: [],
          instrumentInput: "",
          quantities: {},
        },
        () => this.fetchInstruments("")
      );
    }
  }

  private fetchClients(filter: string) {
    this.setState({ clientLoading: true });
    clearTimeout(this.clientFetchDebounce!);
    this.clientFetchDebounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("AccessToken")!;
        const list = await this.controller.getResellerClients(token, {
          filter,
          limit: 20,
        });
        this.setState({ clientOptions: list.data });
      } catch (err) {
        console.error(err);
        this.setState({ clientOptions: [] });
      } finally {
        this.setState({ clientLoading: false });
      }
    }, 300);
  }

  private fetchInstruments(filter: string) {
    this.setState({ instrumentLoading: true });
    clearTimeout(this.instrumentFetchDebounce!);
    this.instrumentFetchDebounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("AccessToken")!;
        const instruments = await this.controller.getManagedInstruments(token, {
          filter,
          limit: 40,
          page: 1,
        });
        this.setState({ instrumentOptions: instruments });
      } catch (err) {
        console.error(err);
        this.setState({ instrumentOptions: [] });
      } finally {
        this.setState({ instrumentLoading: false });
      }
    }, 300);
  }

  private handleClientInputChange = (_: any, value: string) => {
    this.setState({ clientInput: value }, () => {
      if (!this.props.preselectedClient) {
        this.fetchClients(value);
      }
    });
  };

  private handleInstrumentInputChange = (_: any, value: string) => {
    this.setState({ instrumentInput: value }, () => {
      this.fetchInstruments(value);
    });
  };

  private handleConfirm = () => {
    const { selectedClient, selectedInstruments, quantities } = this.state;
    if (selectedClient && selectedInstruments.length > 0) {
      const items = selectedInstruments.map((inst) => ({
        instrumentId: inst.id,
        quantity: Math.max(1, Math.floor(quantities[inst.id] ?? 1)),
      }));

      this.props.onConfirm({
        clientId: selectedClient.clientId,
        clientUid: selectedClient.clientUid,
        items,
      });
    }
  };

  render() {
    const { isOpen, isLoading, preselectedClient, onClose } = this.props;
    const {
      selectedClient,
      clientInput,
      clientOptions,
      clientLoading,
      selectedInstruments,
      instrumentInput,
      instrumentOptions,
      instrumentLoading,
      quantities,
    } = this.state;
    const canConfirm =
      !!selectedClient &&
      selectedInstruments.length > 0 &&
      selectedInstruments.every((inst) => {
        const quantity = quantities[inst.id];
        return typeof quantity === "number" && Number.isFinite(quantity) && quantity > 0;
      });


    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth variant="modal">
        <DialogTitle variant="modal">
          <DomainVerificationIcon />
          Distribuir Licenças
        </DialogTitle>
        <DialogContent variant="modal">
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography color="text.secondary">
                Escolha um cliente e os instrumentos que deseja distribuir, informando a quantidade de licenças para cada um.
              </Typography>
              <Autocomplete
                disabled={!!preselectedClient}
                options={clientOptions}
                getOptionLabel={(opt) =>
                  `${opt.clientName} (${opt.clientEmail})`
                }
                noOptionsText="Nenhum cliente encontrado"
                value={selectedClient}
                onChange={(_, v) => this.setState({ selectedClient: v })}
                inputValue={clientInput}
                onInputChange={this.handleClientInputChange}
                loading={clientLoading}
                isOptionEqualToValue={(o, v) => o.clientId === v.clientId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pesquisar cliente por nome ou email..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {clientLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                options={instrumentOptions}
                getOptionLabel={(opt) => opt.title}
                value={selectedInstruments}
                onChange={(_, newValue) => {
                  const newQuantities: Partial<Record<number, number>> = {};
                  newValue.forEach((inst) => {
                    newQuantities[inst.id] = this.state.quantities[inst.id] ?? 1;
                  });
                  this.setState({ selectedInstruments: newValue, quantities: newQuantities });
                }}
                inputValue={instrumentInput}
                onInputChange={this.handleInstrumentInputChange}
                noOptionsText="Nenhum instrumento encontrado"
                loading={instrumentLoading}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pesquisar instrumento..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {instrumentLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Box display="flex" flexDirection="column" gap={2}>
                {selectedInstruments.map((instrument) => (
                  <TextField
                    key={instrument.id}
                    fullWidth
                    label={`Quantidade - ${instrument.title}`}
                    type="number"
                    value={quantities[instrument.id] ?? ""}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      this.setState((prev) => {
                        const nextQuantities = { ...prev.quantities };
                        if (rawValue === "") {
                          delete nextQuantities[instrument.id];
                          return { quantities: nextQuantities };
                        }

                        const parsedValue = Number(rawValue);
                        if (!Number.isFinite(parsedValue)) {
                          return null;
                        }

                        nextQuantities[instrument.id] = parsedValue;
                        return { quantities: nextQuantities };
                      });
                    }}
                    InputProps={{ inputProps: { min: 1, step: 1 } }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions variant="modal" sx={{ justifyContent: "end", flexWrap: "wrap", gap: 1 }}>
          <Button variant="outlined" color="error" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={this.handleConfirm}
            disabled={!canConfirm || isLoading}
          >
            Distribuir
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
